import * as React from 'react';
import { WithTranslation } from 'react-i18next';
import { RootStore } from '../store';
import cloneDeep from 'lodash/cloneDeep';
import { MENU_CONFLICT_MESSAGE } from '../stores/menu_conflict';
import { UI } from '../../core/ui/index';
import { logger } from '@lib/common';

export interface InjectedProps extends WithTranslation {
  theme: T.Core.BaseTheme.ThemeInterface;
  store: RootStore;
}
interface SaveRestaurantOpts {
  before?: () => void;
  process: (r: T.Schema.Restaurant.RestaurantSchema) => { update: T.ObjectAny };
  onFail?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
  successMsg: string;
  errorMsg?: string;
  shouldUpdateStore?: boolean;
  isMenuImport?: boolean;
}
interface SaveRestaurantSilentOpts {
  update: T.ObjectAny;
  backup: T.Schema.Restaurant.RestaurantSchema;
  before: () => void;
  onFail: () => void;
  onSuccess: () => void;
  onError: () => void;
  successMsg?: string;
  errorMsg?: string;
}

interface SaveWebsiteOpts {
  before: () => void;
  process: (w: T.Schema.Website.WebsiteSchema) => { update: T.ObjectAny };
  onFail: () => void;
  onSuccess: () => void;
  onError: () => void;
  successMsg: string;
}

export class MobxComponent<Props = {}, State = {}> extends React.Component<
  Props,
  State
> {
  get injected() {
    return this.props as InjectedProps & Props;
  }

  async saveRestaurant(opts: SaveRestaurantOpts) {
    try {
      if (opts.before) opts.before();
      const { store } = this.injected;
      let r = cloneDeep(store.restaurant!);
      const _id = r._id;
      const { update } = opts.process(r);
      if (store.isNewMenuSchema && opts.isMenuImport) {
        const _id = store.restaurant!._id;
        const freshRes = await store.api.menu_import({
          restaurant_id: _id,
          // @ts-ignore
          menu: update
        });
        if (freshRes.error) {
          UI.notification.error(freshRes.message);
          if (opts.onFail) opts.onFail();
          return;
        } else {
          UI.notification.success(opts.successMsg);
          if (opts.onSuccess) opts.onSuccess();
        }
      } else {
        const res = await store.api.restaurant_update({
          _id,
          update,
        });
        if (res.outcome) {
          // A menu conflict is not a failure to report in red: the save was
          // refused on purpose, the dialog explains who changed it and offers
          // what to do, and an error alert over the top reads as a bug.
          const refused = res as T.API.MenuConflictResponse;
          if (refused.conflict === 'menu_version') {
            UI.notification.warning(MENU_CONFLICT_MESSAGE);
            // The same save, expecting what the server reported. Registered
            // here because finishing it is this site's job: commit the edited
            // restaurant and tell the form it went through.
            store.menu_conflict.offerOverwrite(async () => {
              const retry = await store.api.restaurant_update({
                _id,
                update,
                expected_menu_version: refused.menu_version,
              });
              if (retry.outcome) throw retry;
              UI.notification.success(opts.successMsg);
              store.setRestaurant(r);
              if (opts.onSuccess) opts.onSuccess();
              if (opts.shouldUpdateStore) {
                store.updateRestaurant(update.$set || {});
              }
            });
          } else {
            UI.notification.error(res.message);
          }
          if (opts.onFail) opts.onFail();
        } else {
          UI.notification.success(opts.successMsg);
          store.setRestaurant(r);
          if (opts.onSuccess) opts.onSuccess();
          if (opts.shouldUpdateStore) {
            store.updateRestaurant(update.$set || {});
          }
        }
      }
    } catch (e) {
      logger.captureException(e);
      UI.notification.error(
        opts.errorMsg || 'An error occurred, try again or contact us'
      );
      if (opts.onError) opts.onError();
    }
  }

  async saveWebsite(opts: SaveWebsiteOpts) {
    try {
      opts.before();
      const w = cloneDeep(this.injected.store.website!);
      const _id = w._id;
      const { update } = opts.process(w);
      const res = await this.injected.store.api.website_update({ _id, update });
      if (res.outcome) {
        UI.notification.error(res.message);
        opts.onFail();
      } else {
        UI.notification.success(opts.successMsg);
        this.injected.store.setWebsite(w);
        opts.onSuccess();
      }
    } catch (e) {
      logger.captureException(e);
      UI.notification.error('An error occurred, try again or contact us');
      opts.onError();
    }
  }

  async saveRestaurantSilent(opts: SaveRestaurantSilentOpts) {
    opts.before();
    try {
      const _id = this.injected.store.restaurant!._id;
      const res = await this.injected.store.api.restaurant_update({
        _id,
        update: opts.update,
      });
      if (res.outcome) {
        UI.notification.error(res.message);
        this.injected.store.setRestaurant(opts.backup);
        opts.onFail();
      } else {
        if (opts.successMsg) {
          UI.notification.success(opts.successMsg);
        }
        opts.onSuccess();
      }
    } catch (e) {
      logger.captureException(e);
      UI.notification.error(
        opts.errorMsg || 'An error occurred, try again or contact us'
      );
      this.injected.store.setRestaurant(opts.backup);
      opts.onError();
    }
  }
}
